import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import { MenuItem } from '../../../../../core/api/menu/menu.types';
import { analyticsService } from '../../../../../core/services';
import { flagService } from '../../../../../core/services/flagService';
import { QuantityControlType } from '../../../../../core/types/basket.types';
import {
  CROSS_SELL_DESKTOP_NUMBER_OF_MENU_ITEMS,
  CROSS_SELL_MOBILE_NUMBER_OF_MENU_ITEMS,
} from '../constants';

interface UseCrossSellList {
  crossSellList: MenuItem[];
  isDesktopScreen: boolean;
  onHandleAddItem: (menuItem: MenuItem) => void;
  showCrossSellList: boolean;
}

export const useCrossSellList = (
  addItemToBasket: QuantityControlType['onAddItem'],
  crossSellMenuItems: MenuItem[],
  isLoading: boolean,
  translate: TranslateFunction,
): UseCrossSellList => {
  const isCrossSellDesktopGridViewEnabled = flagService.isFlagOn(
    'crossSellDesktopGridView',
  );

  const theme = useTheme();
  const isDesktopScreen =
    useMediaQuery(theme.breakpoints.up('lg')) &&
    isCrossSellDesktopGridViewEnabled;
  const menuItemsNumberShown = isDesktopScreen
    ? CROSS_SELL_DESKTOP_NUMBER_OF_MENU_ITEMS
    : CROSS_SELL_MOBILE_NUMBER_OF_MENU_ITEMS;

  const { enqueueSnackbar } = useSnackbar();

  const addedCrossSellItemIds = useRef<MenuItem['MenuId'][]>([]);
  const [crossSellList, setCrossSellList] = useState<MenuItem[]>(() => {
    const initialState = crossSellMenuItems.slice(0, menuItemsNumberShown);
    return initialState;
  });

  const showCrossSellList = isLoading || crossSellList.length !== 0;

  useEffect(() => {
    if (crossSellMenuItems.length > 0) {
      filterCrossSellMenuItems();
    }
  }, [crossSellMenuItems.length, menuItemsNumberShown]);

  const trackCrossSellItemAdded = (menuItem: MenuItem): void => {
    const itemIndex = crossSellMenuItems.findIndex(
      (item) => item.MenuItemId === menuItem.MenuItemId,
    );
    const eventData = {
      amp_label: 'Cross-sell added',
      amp_indexOfItem: itemIndex,
      amp_itemPrice: menuItem.ActualPrice,
      amp_menuItemId: menuItem.MenuItemId,
      amp_hasImage: !!menuItem.ImageUrl,
    };

    analyticsService.track(eventData);
  };

  const filterCrossSellMenuItems = (addedMenuItem?: MenuItem): void => {
    const addedMenuItemsIds = addedMenuItem
      ? [...addedCrossSellItemIds.current, addedMenuItem.MenuItemId]
      : addedCrossSellItemIds.current;
    addedCrossSellItemIds.current = addedMenuItemsIds;

    const filteredCrossSellMenuItems = crossSellMenuItems.filter(
      (item) => !addedMenuItemsIds.includes(item.MenuItemId),
    );
    setCrossSellList(filteredCrossSellMenuItems.slice(0, menuItemsNumberShown));
  };

  const onHandleAddItem = (menuItem: MenuItem): void => {
    trackCrossSellItemAdded(menuItem);
    addItemToBasket({
      Count: 1,
      Item: menuItem,
      Options: [],
    });
    filterCrossSellMenuItems(menuItem);
    enqueueSnackbar(translate('Basket_itemAdded'), {
      variant: 'success',
    });
  };

  return {
    crossSellList,
    isDesktopScreen,
    onHandleAddItem,
    showCrossSellList,
  };
};
